// ─────────────────────────────────────────────────────────────────────────────
// CockpitDisplay — Full-bleed Monitor hero (Training Zone Energy System look).
//
// Visual replacement for the previous "3 top tiles + main split + 4 side
// metrics" hero on `WorkoutMonitorV2`. All other Monitor features (force
// balance, stroke visualizer, CSV logger, target buttons, transport
// controls) keep rendering BELOW this component.
//
// Edge-mounted "Training Zone Energy System" glow:
//   • Driven by the live HR strap zone (Karvonen %HRR).
//   • Only visible when a strap is connected (hr > 0).
//   • Pulses faster at higher zones; respects prefers-reduced-motion.
//
// Layout:
//   • Landscape: side metrics flank the hero L/R.
//   • Portrait: ELAP / DIST move ABOVE the hero, CAL / RHY move BELOW (per
//     user spec — better small-screen utilisation).
//
// Theme is driven by the existing app dark/light toggle (no new control).
// Slot picker / long-press / metric assignment all delegate to the existing
// <ClickableMetric> component — no behavioural changes.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import ClickableMetric from './ClickableMetric';
import CheckFactorBar from './CheckFactorBar';
import SessionGoalPie from './SessionGoalPie';
import WBTCrown from './WBTCrown';
import { ThumbsUp } from 'lucide-react';
import useWBTTarget from '../hooks/useWBTTarget';
import { hrrPercent } from '../lib/hrZones';

// ── Pace cue: delta = currentSplit − targetSplit (seconds) ──────────────────
// Negative delta = faster than target (AHEAD); positive = slower (BEHIND).
// Per rower request (2026-06 UX brief): arrow points DOWN when the rower is
// BEHIND their nominated split (yellow/orange), UP when AHEAD of it (green).
// Rowers read "down" as needing to pull harder to bring the split number
// down toward the target line.
export function paceState(delta) {
  if (delta <= -0.3) return { color: '#21B86A', dir: '▲', word: 'AHEAD' };
  if (delta <  0.3)  return { color: '#3FD6A4', dir: '●', word: 'ON TARGET' };
  if (delta <  1.5)  return { color: '#F5C518', dir: '▼', word: 'BEHIND' };
  return                    { color: '#F2711C', dir: '▼', word: 'BEHIND' };
}
function fmtPaceSplit(secondsTotal) {
  const s = Math.max(0, Math.round(secondsTotal));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
const PACE_AUTO_HIDE_MS = 5000;

// Karvonen %HRR thresholds, official CrewZ Athletes Energy Systems colours.
const ZONES = [
  { key: 'UT3', short: 'UT3', zone: 'Utilization 3', full: 'Recovery',             maxPct: 55,  color: '#9AA4AE' },
  { key: 'UT2', short: 'UT2', zone: 'Utilization 2', full: 'Oxygen Utilisation 2', maxPct: 70,  color: '#2F80ED' },
  { key: 'UT1', short: 'UT1', zone: 'Utilization 1', full: 'Oxygen Utilisation 1', maxPct: 80,  color: '#21B86A' },
  { key: 'THR', short: 'THR', zone: 'Threshold',     full: 'Anaerobic Threshold',  maxPct: 85,  color: '#F5C518' },
  { key: 'TR',  short: 'TR',  zone: 'Transport',     full: 'Oxygen Transport',     maxPct: 95,  color: '#F2711C' },
  { key: 'AN',  short: 'AN',  zone: 'Anaerobic',     full: 'Anaerobic',            maxPct: 999, color: '#E5403A' },
];

function zoneForHR(hr, restHR, maxHR) {
  const hrr = hrrPercent(hr, maxHR, restHR);
  if (hrr == null) return null;
  const pct = Math.min(120, hrr);
  for (const z of ZONES) if (pct < z.maxPct) return { ...z, pct };
  return { ...ZONES[ZONES.length - 1], pct };
}

// Watch device orientation via matchMedia. `override` ('landscape' | 'portrait')
// from user settings beats the sensor when set.
function useOrientation(override) {
  // Subscription-driven state for the device orientation media query.
  const [mqlOrientation, setMqlOrientation] = useState(() => {
    if (typeof window === 'undefined') return 'landscape';
    return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
  });
  useEffect(() => {
    const mql = window.matchMedia('(orientation: portrait)');
    const onChange = () => setMqlOrientation(mql.matches ? 'portrait' : 'landscape');
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  // User override beats the sensor. Derive — no extra state needed.
  if (override === 'landscape' || override === 'portrait') return override;
  return mqlOrientation;
}

// Side-metric tile — defined at module scope so React's reconciler keeps
// the same component identity across CockpitDisplay re-renders (prevents
// remounting the ClickableMetric subtree on every parent render).
function SideTile({ slot, defaultId, metricPreferences, allMetrics, compact, onPreferenceChange, orientation, valueStyle }) {
  // In portrait the side tiles sit ABOVE/BELOW the hero, so they're laid
  // out as a horizontal row. Each tile gets a smaller, denser typography so
  // the hero still dominates the screen.
  const txt = orientation === 'portrait'
    ? 'text-lg'
    : (compact ? 'text-2xl landscape:text-3xl' : 'text-4xl');
  return (
    <div className="crewz-cockpit-side-tile">
      <ClickableMetric
        metricType="bottom"
        defaultMetric={metricPreferences[slot] || defaultId}
        allMetrics={allMetrics}
        layout="horizontal"
        textSize={txt}
        valueStyle={valueStyle}
        onPreferenceChange={(id) => onPreferenceChange(slot, id)}
      />
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.darkMode
 * @param {boolean} props.cockpitMode — true = fullscreen-style overlay
 *                                       (fills 100dvh × 100dvw)
 * @param {string}  props.modeLabel — e.g. "MODE 2 · PHONE ONLY"
 * @param {number}  props.heartRate
 * @param {number}  props.restingHR
 * @param {number}  props.maxHR
 * @param {boolean} props.strapConnected
 * @param {object}  props.metricPreferences
 * @param {object}  props.allMetrics
 * @param {object}  props.metricDefaults — { top1, top2, top3, sideLeft1, sideLeft2, sideRight1, sideRight2, mainDisplay }
 * @param {Function} props.onPreferenceChange — (slot, metricId) => void
 * @param {string|null} props.orientationOverride — 'landscape' | 'portrait' | null
 * @param {object|null} props.strokeData — passed to mainDisplay ClickableMetric
 * @param {boolean} props.compact — collapse paddings (active workout)
 * @param {Function} [props.onSwipeDown] — fired on a deliberate downward swipe
 *        from the top edge while in fullscreen mode (used to exit fullscreen).
 * @param {boolean} [props.paceTargetOn] — manual pace target enabled
 * @param {number}  [props.paceTargetSplit] — target 500m split in seconds
 * @param {number}  [props.currentSplitSec] — live 500m split in seconds
 * @param {boolean} [props.inProgram] — true when a Program owns the target
 * @param {object}  [props.programStep] — { index, total, label, target }
 * @param {Function} [props.onTogglePaceTarget] — toggle manual switch
 * @param {Function} [props.onChangeTargetSplit] — (delta:int) => void
 * @param {boolean} [props.showCheckFactor] — legacy prop; when true and
 *        leftTileDisplay is not provided this is interpreted as
 *        "check_factor". Prefer `leftTileDisplay` going forward.
 * @param {Array}  [props.checkFactorStrokeData] — same `strokeData` array the
 *        CheckFactorGraph consumes; feeds the CF% bar's per-stroke
 *        calculation.
 * @param {string} [props.leftTileDisplay] — one of "off" / "check_factor" /
 *        "session_goal". Controls what renders in the left flanking
 *        tile. Session Goal is force-enabled on Mode 3 (Ergo) by the
 *        parent regardless of user preference.
 * @param {object} [props.sessionGoalProps] — passthrough props for the
 *        Session Goal widget (goal state, callbacks, live metrics).
 */
export default function CockpitDisplay({
  darkMode,
  cockpitMode = false,
  modeLabel,
  heartRate,
  restingHR = 60,
  maxHR = 185,
  strapConnected,
  metricPreferences,
  allMetrics,
  metricDefaults,
  onPreferenceChange,
  orientationOverride = null,
  strokeData = null,
  compact = false,
  onSwipeDown,
  paceTargetOn = false,
  paceTargetSplit = 115,
  currentSplitSec = 0,
  inProgram = false,
  programStep = null,
  onTogglePaceTarget,
  onChangeTargetSplit,
  showCheckFactor = false,
  checkFactorStrokeData = [],
  leftTileDisplay = null,
  sessionGoalProps = null,
  boatType = null,
  appMode = null,
  tileFontStep = 0,
}) {
  // ── WBT Crown wiring (CrewZ v3 spec) ────────────────────────────────────
  // Mode 3 (Ergo) compares split/500m; every other on-water mode compares
  // instantaneous speed. Silent when target is null (no matching WBT).
  const wbtMode = appMode === 3 ? 'ergo' : 'on_water';
  const { target: wbtTarget } = useWBTTarget(wbtMode, boatType);
  // `allMetrics.meters_per_second` may be a number (moving) OR the string
  // '—' (zero-velocity gate — see WorkoutMonitorV2 L1737). Parse defensively
  // so the crown reads real m/s in both cases. This was the source of the
  // "crown never fires even in boost" bug — the earlier code read
  // `allMetrics?.speed` (no such key), so wbtLive was permanently 0.
  const rawSpeed = allMetrics?.meters_per_second;
  const speedMs = typeof rawSpeed === 'number'
    ? rawSpeed
    : (typeof rawSpeed === 'string' ? parseFloat(rawSpeed) : NaN);
  const wbtLive = wbtMode === 'ergo'
    ? currentSplitSec
    : (Number.isFinite(speedMs) ? speedMs : 0);
  const orientation = useOrientation(orientationOverride);
  const zone = strapConnected ? zoneForHR(heartRate, restingHR, maxHR) : null;
  const glowColor = zone?.color || 'transparent';
  // Breathe period: ~4.2s in UT3, ~1.4s at AN. Higher zone → faster pulse.
  const breathePeriod = zone ? Math.max(1.4, 4.2 - (zone.pct / 100) * 3.2) : 0;
  const showGlow = !!zone;

  // ── Pace overlay derived state ────────────────────────────────────────────
  // The pace overlay shows when EITHER (a) a program is loaded — the program
  // owns the target — OR (b) the manual switch is on. The ribbon meaning is
  // carried by position FIRST (centre = on target, left = behind, right =
  // ahead) and colour second, so it never blurs with the HR edge glow.
  const effectiveTarget = inProgram && programStep ? programStep.target : paceTargetSplit;
  const showPaceOverlay = inProgram || paceTargetOn;
  const paceDelta = (currentSplitSec || 0) - effectiveTarget;          // +ve = slower than target
  const pace = paceState(paceDelta);
  // Marker position on a 0..100 scale. ±4s saturates the ribbon ends.
  // Negative delta (faster) → marker to the right (AHEAD side).
  const paceMarkerPos = 50 + Math.max(-50, Math.min(50, (-paceDelta / 4) * 50));
  const paceFillLeft  = Math.min(50, paceMarkerPos);
  const paceFillWidth = Math.abs(50 - paceMarkerPos);
  const paceDeltaLabel = `${paceDelta > 0 ? '+' : paceDelta < 0 ? '−' : ''}${Math.abs(paceDelta).toFixed(1)}s`;
  // Only render the direction marker when we're actually showing a target —
  // otherwise the split number stands alone like before.
  const directionGlyph = showPaceOverlay && currentSplitSec > 0 ? pace.dir : null;

  // Auto-hide pace controls 5s after the last interaction. Initial state is
  // COLLAPSED — the rower sees only the ambient ribbon (+ a tap hint) until
  // they deliberately touch the pace area. This keeps the screen clean and
  // avoids the controls competing with the live metrics during a session.
  const [paceCtrlVisible, setPaceCtrlVisible] = useState(false);
  const paceHideTimerRef = useRef(null);
  const pokePaceControls = () => {
    setPaceCtrlVisible(true);
    if (paceHideTimerRef.current) clearTimeout(paceHideTimerRef.current);
    paceHideTimerRef.current = setTimeout(() => setPaceCtrlVisible(false), PACE_AUTO_HIDE_MS);
  };
  useEffect(() => () => {
    if (paceHideTimerRef.current) clearTimeout(paceHideTimerRef.current);
  }, []);

  // ── Swipe-down-to-exit-fullscreen gesture ─────────────────────────────────
  // Mounted only while in cockpit-fullscreen mode. Triggers `onSwipeDown` when
  // the user starts a touch in the top 25% of the screen and drags down by
  // >= SWIPE_MIN_DY px within SWIPE_MAX_MS ms, with a vertical/horizontal
  // ratio > 1.5 (so a slow scroll or horizontal swipe never fires the exit).
  const swipeRef = useRef({ active: false, x: 0, y: 0, t: 0 });
  // Visual swipe-down hint. Shown briefly the first time the cockpit enters
  // fullscreen during this page load (initial state derived from cockpitMode).
  // Auto-hides after 2.5s. Subsequent re-entries don't re-show the hint —
  // the gesture itself stays armed for the whole session.
  const [swipeHintVisible, setSwipeHintVisible] = useState(cockpitMode);
  useEffect(() => {
    if (!swipeHintVisible) return undefined;
    const hideTimer = setTimeout(() => setSwipeHintVisible(false), 2500);
    return () => clearTimeout(hideTimer);
  }, [swipeHintVisible]);

  // Swipe-down-to-exit gesture — armed for the whole time `cockpitMode` is
  // truthy. Detached from the hint timer above so the gesture works even
  // after the visual hint has faded.
  useEffect(() => {
    if (!cockpitMode || typeof onSwipeDown !== 'function') return undefined;

    const SWIPE_MIN_DY = 80;
    const SWIPE_MAX_MS = 600;
    const TOP_ZONE_FRAC = 0.25;     // start in top 25% of screen
    const VH_RATIO_MIN = 1.5;       // vertical at least 1.5× horizontal

    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      // Only arm the gesture when the touch begins in the top zone — avoids
      // hijacking taps on metric tiles in the middle / bottom of the screen.
      if (t.clientY > window.innerHeight * TOP_ZONE_FRAC) {
        swipeRef.current.active = false;
        return;
      }
      swipeRef.current = { active: true, x: t.clientX, y: t.clientY, t: Date.now() };
    };
    const onTouchEnd = (e) => {
      const s = swipeRef.current;
      if (!s.active) return;
      swipeRef.current.active = false;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      const dt = Date.now() - s.t;
      if (dt > SWIPE_MAX_MS) return;
      if (dy < SWIPE_MIN_DY) return;
      if (Math.abs(dy) < Math.abs(dx) * VH_RATIO_MIN) return;
      // Haptic confirmation BEFORE the visual transition fires so the user
      // gets immediate tactile feedback while the fullscreen exit is in
      // flight. Wrapped in a try so any browser without the Vibration API
      // (iOS Safari today) silently no-ops.
      try { navigator.vibrate?.(15); } catch (_e) { /* noop */ }
      try { onSwipeDown(); } catch (_e) { /* noop */ }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [cockpitMode, onSwipeDown]);

  // Resolve the left-tile display mode. `leftTileDisplay` (new) wins; fall
  // back to the legacy `showCheckFactor` flag for callers that haven't
  // migrated yet.
  const resolvedLeftTile = leftTileDisplay
    || (showCheckFactor ? 'check_factor' : 'off');
  const hasLeftTilePanel = resolvedLeftTile === 'check_factor' || resolvedLeftTile === 'session_goal';
  // Landscape + Session Goal: thumb sits in the mirror column so it's symmetrical with the pie.
  const mirrorPaceDir = resolvedLeftTile === 'session_goal' && orientation === 'landscape';
  const paceDirEl = (
    <div
      className={`crewz-cockpit-hero-arrow ${mirrorPaceDir ? 'in-col' : 'right'}`}
      data-testid="crewz-cockpit-pace-dir"
      aria-hidden="true"
    >
      {directionGlyph === '▲' ? (
        <ThumbsUp
          className="crewz-cockpit-pace-thumb"
          data-testid="crewz-cockpit-pace-thumb"
          fill="#21B86A"
          stroke="#000"
          strokeWidth={2.25}
        />
      ) : directionGlyph}
    </div>
  );

  const rootClass = [
    'crewz-cockpit',
    darkMode ? 'dark' : 'light',
    orientation,
    cockpitMode ? 'cockpit-fullscreen' : 'cockpit-inline',
    compact ? 'cockpit-compact' : '',
    hasLeftTilePanel ? 'cf-on' : '',
    resolvedLeftTile === 'session_goal' ? 'goal-on' : '',
  ].filter(Boolean).join(' ');

  // Pass through common props to the module-scope SideTile.
  const sideTileProps = { metricPreferences, allMetrics, compact, onPreferenceChange, orientation };

  // Orientation-aware typography. In portrait we shrink the top tiles + side
  // tiles dramatically so the hero (main split) gets the lion's share of the
  // viewport — otherwise the 3 top tiles eat ~half the screen.
  const topTextSize = orientation === 'portrait'
    ? 'text-2xl'
    : (compact ? 'text-3xl landscape:text-4xl' : 'text-5xl');
  const heroTextSize = orientation === 'portrait'
    ? 'text-8xl'
    : (compact ? 'text-6xl landscape:text-7xl' : 'text-8xl md:text-9xl');
  const topTilePad = orientation === 'portrait' ? 'p-1' : (compact ? 'p-2 landscape:p-3' : 'p-2');

  // ── User tile font-size control ───────────────────────────────────────────
  // `tileFontStep` (0..4) enlarges the numbers in the three top tiles + the
  // two side tiles only (the hero/main split is untouched). Each step is a
  // clear, noticeable bump; capped at 4 so the cockpit layout stays intact.
  // At step 0 we pass `undefined` so the original Tailwind sizing is used
  // verbatim (no regression). Base rems mirror the Tailwind classes above so
  // step 0 and step 1 line up continuously, and `rem` keeps parity with
  // Tailwind regardless of the root font-size.
  const clampedFontStep = Math.max(0, Math.min(4, tileFontStep || 0));
  const topBaseRem = orientation === 'portrait' ? 1.5 : (compact ? 2.25 : 3.0);
  const sideBaseRem = orientation === 'portrait' ? 1.125 : (compact ? 1.875 : 2.25);
  const tileFontScale = 1 + clampedFontStep * 0.15;
  const topValueStyle = clampedFontStep > 0
    ? { fontSize: `${(topBaseRem * tileFontScale).toFixed(3)}rem`, lineHeight: 1.05 }
    : undefined;
  const sideValueStyle = clampedFontStep > 0
    ? { fontSize: `${(sideBaseRem * tileFontScale).toFixed(3)}rem`, lineHeight: 1.05 }
    : undefined;

  return (
    <div
      className={rootClass}
      data-testid="crewz-cockpit"
      style={{
        '--glow': glowColor,
        '--breathe': `${breathePeriod}s`,
        '--pace': pace.color,
      }}
    >
      {/* Edge glow layer — pinned to the cockpit edges. In fullscreen mode
          these are the viewport edges; inline mode they hug the section. */}
      <div
        className={`crewz-cockpit-glow ${showGlow ? 'live' : ''}`}
        aria-hidden="true"
        data-testid="crewz-cockpit-glow"
      />

      <div className="crewz-cockpit-grid">
        {/* Mode badge */}
        <div className="crewz-cockpit-mode" data-testid="crewz-cockpit-mode-badge">
          {modeLabel}
        </div>

        {/* Swipe-down-to-exit hint — only in fullscreen mode, auto-hides
            after 2.5s but the gesture itself stays armed for the whole
            session. The visual handle (the small grabber bar) is purely
            decorative; the listener is window-wide. */}
        {cockpitMode && (
          <div
            data-testid="crewz-cockpit-swipe-hint"
            className={`crewz-cockpit-swipe-hint ${swipeHintVisible ? 'visible' : ''}`}
            aria-hidden="true"
          >
            <div className="crewz-cockpit-swipe-handle" />
            <div className="crewz-cockpit-swipe-label">Swipe down to exit</div>
          </div>
        )}

        {/* Top three metric tiles */}
        <div className="crewz-cockpit-top crewz-cockpit-top-1" data-testid="crewz-cockpit-top-1">
          <ClickableMetric
            metricType="small"
            defaultMetric={metricPreferences.top1 || metricDefaults.top1 || 'meters_per_second'}
            allMetrics={allMetrics}
            className={`text-center ${topTilePad}`}
            textSize={topTextSize}
            valueStyle={topValueStyle}
            onPreferenceChange={(id) => onPreferenceChange('top1', id)}
          />
        </div>
        <div className="crewz-cockpit-top crewz-cockpit-top-2" data-testid="crewz-cockpit-top-2">
          <ClickableMetric
            metricType="small"
            defaultMetric={metricPreferences.top2 || metricDefaults.top2 || 'heart_rate'}
            allMetrics={allMetrics}
            className={`text-center ${topTilePad}`}
            textSize={topTextSize}
            valueStyle={topValueStyle}
            highlight={!!zone}
            onPreferenceChange={(id) => onPreferenceChange('top2', id)}
          />
        </div>
        <div className="crewz-cockpit-top crewz-cockpit-top-3" data-testid="crewz-cockpit-top-3">
          <ClickableMetric
            metricType="small"
            defaultMetric={metricPreferences.top3 || metricDefaults.top3 || 'stroke_rate'}
            allMetrics={allMetrics}
            className={`text-center ${topTilePad}`}
            textSize={topTextSize}
            valueStyle={topValueStyle}
            onPreferenceChange={(id) => onPreferenceChange('top3', id)}
          />
        </div>

        {/* Hero row: side metrics + main display.
            Landscape → flank L/R.  Portrait → stack above/below. */}
        <div className="crewz-cockpit-hero-row">
          <div className="crewz-cockpit-side crewz-cockpit-side-left" data-testid="crewz-cockpit-side-left">
            <SideTile slot="sideLeft1" defaultId={metricDefaults.sideLeft1 || 'elapsed_time'} valueStyle={sideValueStyle} {...sideTileProps} />
            <SideTile slot="sideLeft2" defaultId={metricDefaults.sideLeft2 || 'distance'} valueStyle={sideValueStyle} {...sideTileProps} />
          </div>

          {/* Left flanking tile — universal slot. Renders one of:
              • CheckFactorBar (CF%)
              • SessionGoalPie
              …based on `resolvedLeftTile`. When "off", the column
              collapses out of the grid entirely so the sideLeft +
              hero + sideRight fall back to the original 3-column
              layout. Grid template CSS handles the collapse. */}
          {hasLeftTilePanel && (
            <div className="crewz-cockpit-cf" data-testid="crewz-cockpit-cf-panel">
              {resolvedLeftTile === 'session_goal' && sessionGoalProps ? (
                <SessionGoalPie {...sessionGoalProps} />
              ) : (
                <CheckFactorBar strokeData={checkFactorStrokeData} />
              )}
            </div>
          )}

          {mirrorPaceDir && (
            <div className="crewz-cockpit-cfR" data-testid="crewz-cockpit-cfR-panel">
              {showPaceOverlay && directionGlyph && paceDirEl}
            </div>
          )}

          <div className={`crewz-cockpit-hero ${showPaceOverlay ? 'with-pace' : ''}`}>
            {/* Pace-target overlay — visible only while a target is
                active. Left + right chevron arrows tint per pace state
                (green ▲ = AHEAD, amber ● = ON TARGET, orange ▼ =
                BEHIND). A "TARGET · 1:55" chip sits above the split
                number and a big coloured "−16s AHEAD" delta chip sits
                below it, so the rower sees BOTH the target they're
                shooting for AND how far off they are without hunting
                for the small pace ribbon below the hero. */}
            {showPaceOverlay && directionGlyph && !mirrorPaceDir && paceDirEl}
            {/* NOTE: The TARGET chip above the split and the delta pill
                ("−4.0s AHEAD") below have been intentionally removed
                per product brief 2026-02-19. The pace-track ribbon at
                the bottom of the cockpit already conveys the same
                behind/ahead position and the pace-target stepper
                already shows the numeric target — the two overlays
                were redundant clutter. */}
            <div className="crewz-cockpit-hero-metric" style={{ position: 'relative' }}>
              <ClickableMetric
                metricType="large"
                defaultMetric={metricPreferences.mainDisplay || metricDefaults.mainDisplay || 'split'}
                allMetrics={allMetrics}
                className="text-center"
                textSize={heroTextSize}
                onPreferenceChange={(id) => onPreferenceChange('mainDisplay', id)}
                strokeData={strokeData}
              />
              {/* WBT Crown — inline right of the hero value per v3 spec §5.
                  Absolutely positioned so it hugs the value without
                  reflowing the metric layout. */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '-3.2em',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
                data-testid="wbt-crown-slot"
              >
                <WBTCrown liveValue={wbtLive} target={wbtTarget} mode={wbtMode} />
              </div>
            </div>
            {/* Delta pill removed per brief 2026-02-19 — see note above. */}
          </div>

          <div className="crewz-cockpit-side crewz-cockpit-side-right">
            <SideTile slot="sideRight1" defaultId={metricDefaults.sideRight1 || 'calories'} valueStyle={sideValueStyle} {...sideTileProps} />
            <SideTile slot="sideRight2" defaultId={metricDefaults.sideRight2 || 'rhythm_ratio'} valueStyle={sideValueStyle} {...sideTileProps} />
          </div>

          {/* ── Pace-vs-target row ──────────────────────────────────────────
              Full-width row beneath the hero + side metrics.
                • Ribbon shows only while a target is active (or a Program owns it)
                • Manual controls (or program chip) are ALWAYS reachable so the
                  rower can turn the target on/off from the cockpit itself.
                • Manual controls auto-hide 5 s after the last interaction;
                  collapsed state still shows a tap hint / ghost button so the
                  surface is discoverable. */}
          <div
            className="crewz-cockpit-pacebar"
            data-testid="crewz-cockpit-pacebar"
            onClick={pokePaceControls}
            onTouchStart={pokePaceControls}
          >
            {showPaceOverlay && (
              <div className="crewz-cockpit-pace-row">
                <span className="crewz-cockpit-pace-end l" aria-hidden="true">BEHIND</span>
                <div className="crewz-cockpit-pace-track" data-testid="crewz-cockpit-pace-track">
                  <div className="crewz-cockpit-pace-center" aria-hidden="true" />
                  <div
                    className="crewz-cockpit-pace-fill"
                    style={{ left: `${paceFillLeft}%`, width: `${paceFillWidth}%` }}
                  />
                  <div
                    className="crewz-cockpit-pace-marker"
                    data-testid="crewz-cockpit-pace-marker"
                    style={{ left: `${paceMarkerPos}%` }}
                  />
                </div>
                <span className="crewz-cockpit-pace-end r" aria-hidden="true">AHEAD</span>
              </div>
            )}

            {inProgram && programStep ? (
              /* Program owns the target. Read-only chip — no manual control. */
              <div className="crewz-cockpit-pace-prog" data-testid="crewz-cockpit-pace-program-chip">
                <span className="crewz-cockpit-pace-prog-dot" aria-hidden="true" />
                PROGRAM · STEP {programStep.index + 1}/{programStep.total} · {programStep.label}
                <strong>{fmtPaceSplit(effectiveTarget)}</strong>
                <span className="crewz-cockpit-pace-prog-delta">{paceDeltaLabel} · {pace.word}</span>
              </div>
            ) : (
              <>
                <div className={`crewz-cockpit-pace-ctl ${paceCtrlVisible ? '' : 'hidden'}`}>
                  <button
                    type="button"
                    className={`crewz-cockpit-pace-switch ${paceTargetOn ? 'on' : ''}`}
                    data-testid="crewz-cockpit-pace-switch"
                    aria-pressed={paceTargetOn}
                    onClick={(e) => { e.stopPropagation(); onTogglePaceTarget?.(); pokePaceControls(); }}
                  >
                    <span className="crewz-cockpit-pace-switch-knob" aria-hidden="true" />
                    <span className="crewz-cockpit-pace-switch-text">Pace Target</span>
                  </button>

                  {paceTargetOn ? (
                    <div className="crewz-cockpit-pace-target">
                      <button
                        type="button"
                        className="crewz-cockpit-pace-step"
                        data-testid="crewz-cockpit-pace-faster"
                        aria-label="Make target faster (decrease split)"
                        onClick={(e) => { e.stopPropagation(); onChangeTargetSplit?.(-1); pokePaceControls(); }}
                      >−</button>
                      <div className="crewz-cockpit-pace-target-val">
                        <div className="crewz-cockpit-pace-target-num" data-testid="crewz-cockpit-pace-target-num">
                          {fmtPaceSplit(paceTargetSplit)}
                        </div>
                        <div className="crewz-cockpit-pace-target-cap">
                          {paceDeltaLabel} · {pace.word}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="crewz-cockpit-pace-step"
                        data-testid="crewz-cockpit-pace-slower"
                        aria-label="Make target slower (increase split)"
                        onClick={(e) => { e.stopPropagation(); onChangeTargetSplit?.(1); pokePaceControls(); }}
                      >+</button>
                    </div>
                  ) : (
                    <div className="crewz-cockpit-pace-hint">Tap to set a target split</div>
                  )}
                </div>

                {/* Collapsed-state affordances. Always interactive so the
                    user can re-open the controls without hunting. */}
                {!paceCtrlVisible && paceTargetOn && (
                  <div className="crewz-cockpit-pace-tap" data-testid="crewz-cockpit-pace-tap-hint">
                    Tap to adjust target
                  </div>
                )}
                {!paceCtrlVisible && !paceTargetOn && (
                  <button
                    type="button"
                    className="crewz-cockpit-pace-ghost"
                    data-testid="crewz-cockpit-pace-ghost"
                    onClick={(e) => { e.stopPropagation(); onTogglePaceTarget?.(); pokePaceControls(); }}
                  >＋ Pace target</button>
                )}

                {paceTargetOn && (
                  <div className="crewz-cockpit-pace-src">
                    Manual target · overlay, session unaffected
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Zone chip — full zone name + accent dot. */}
        {zone && (
          <div
            className="crewz-cockpit-zone-chip"
            data-testid="crewz-cockpit-zone-chip"
            style={{ '--c': zone.color }}
          >
            <span className="crewz-cockpit-zone-dot" />
            {zone.zone} · {zone.full}
          </div>
        )}
      </div>

      {/* Scoped styles — kept in component for portability. */}
      <style>{COCKPIT_CSS}</style>
    </div>
  );
}

// ── Scoped CSS ──────────────────────────────────────────────────────────────
const COCKPIT_CSS = `
.crewz-cockpit {
  position: relative;
  width: 100%;
  border-radius: 18px;
  overflow: hidden;
  isolation: isolate;
  --c-bg:     #ffffff;
  --c-panel:  #f7f8fb;
  --c-line:   #e4e8ee;
  --c-text:   #16222e;
  --c-dim:    #6b7a88;
  --c-hero:   #0f1a24;
}
.crewz-cockpit.dark {
  --c-bg:     #0a0e12;
  --c-panel:  #121820;
  --c-line:   #1e2a36;
  --c-text:   #eaf2f8;
  --c-dim:    #7c8b99;
  --c-hero:   #ffffff;
}
.crewz-cockpit.cockpit-inline {
  background: var(--c-bg);
  border: 1px solid var(--c-line);
}
.crewz-cockpit.cockpit-fullscreen {
  position: fixed;
  inset: 0;
  width: 100dvw;
  height: 100dvh;
  /* fallback for browsers without dvw/dvh */
  width: 100vw;
  height: 100vh;
  width: 100dvw;
  height: 100dvh;
  border-radius: 0;
  z-index: 60;
  background: var(--c-bg);
}
/* Edge glow — pinned to cockpit edges via inset shadows. */
.crewz-cockpit-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.6s ease;
  border-radius: inherit;
}
.crewz-cockpit-glow.live {
  opacity: 1;
  box-shadow:
    inset 0 0 0 1.5px var(--glow),
    inset 0 0 22px 2px var(--glow),
    inset 0 0 60px 6px color-mix(in srgb, var(--glow) 55%, transparent),
    inset 0 0 110px 18px color-mix(in srgb, var(--glow) 22%, transparent);
  animation: crewz-cockpit-breathe var(--breathe, 3s) ease-in-out infinite;
}
@keyframes crewz-cockpit-breathe {
  0%,100% { filter: brightness(.82) saturate(1); }
  50%     { filter: brightness(1.25) saturate(1.15); }
}
@media (prefers-reduced-motion: reduce) {
  .crewz-cockpit-glow.live {
    animation: none;
    filter: brightness(1);
  }
}

/* Grid — uses fr units so it always fits the cockpit box. */
.crewz-cockpit-grid {
  position: relative;
  z-index: 2;
  display: grid;
  gap: 10px;
  padding: 12px;
  /* respect safe areas in fullscreen mode */
}
/* Portrait shrinks all gutters so the hero gets max real estate. */
.crewz-cockpit.portrait .crewz-cockpit-grid { gap: 6px; padding: 8px; }
.crewz-cockpit.portrait .crewz-cockpit-top { padding: 0; }
/* Inline portrait — hero row claims remaining vertical space below the
   slim top-tile row. Tuned to 60% of cockpit (hero ≈ 40dvh of ~65dvh). */
.crewz-cockpit.cockpit-inline.portrait .crewz-cockpit-grid {
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto auto minmax(40dvh, 1fr) auto;
  min-height: 65dvh;
}
.crewz-cockpit.cockpit-inline.portrait .crewz-cockpit-hero-row {
  min-height: 40dvh;
}
.crewz-cockpit.cockpit-fullscreen .crewz-cockpit-grid {
  padding:
    calc(env(safe-area-inset-top, 0px)    + 14px)
    calc(env(safe-area-inset-right, 0px)  + 14px)
    calc(env(safe-area-inset-bottom, 0px) + 14px)
    calc(env(safe-area-inset-left, 0px)   + 14px);
  height: 100%;
  grid-template-columns: repeat(3, 1fr);
}
.crewz-cockpit.cockpit-fullscreen.landscape .crewz-cockpit-grid {
  grid-template-rows: auto auto minmax(0, 1fr) auto;
}
/* Bug 2026-06: In fullscreen (both landscape AND portrait) the auto-row for
   the three top stat tiles (SPEED / HR / STROKE RATE) was being visually
   stretched by the grid, leaving the centred content floating with big empty
   space above the number. Force the tiles to size strictly to their content
   so the layout matches inline mode. */
.crewz-cockpit.cockpit-fullscreen .crewz-cockpit-top {
  align-self: start;
  max-height: max-content;
}
.crewz-cockpit.cockpit-fullscreen.portrait .crewz-cockpit-grid {
  grid-template-rows: auto auto minmax(0, 1fr) auto;
}
.crewz-cockpit.cockpit-inline .crewz-cockpit-grid {
  grid-template-columns: repeat(3, 1fr);
}

.crewz-cockpit-mode {
  grid-column: 1 / -1;
  justify-self: center;
  color: var(--c-dim);
  letter-spacing: .18em;
  /* Height-match the bottom zone chip so the top mode bar and the
     bottom "UTILIZATION · RECOVERY" chip read as a matched pair. */
  font-size: clamp(11px, 1.6vw, 14px);
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1;
  padding: 6px 12px;
  display: flex;
  align-items: center;
}

.crewz-cockpit-top {
  background: var(--c-panel);
  border: 1px solid var(--c-line);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}
.crewz-cockpit-top-1 { grid-column: 1; }
.crewz-cockpit-top-2 { grid-column: 2; }
.crewz-cockpit-top-3 { grid-column: 3; }

/* Hero row spans all three columns. Internal layout differs by orientation. */
.crewz-cockpit-hero-row {
  grid-column: 1 / -1;
  background: var(--c-panel);
  border: 1px solid var(--c-line);
  border-radius: 14px;
  display: grid;
  gap: 12px;
  padding: 14px;
  min-height: 0;
}
.crewz-cockpit.landscape .crewz-cockpit-hero-row {
  grid-template-columns: minmax(120px, 0.9fr) minmax(0, 2.4fr) minmax(120px, 0.9fr);
  align-items: center;
  grid-template-areas:
    "sideL hero sideR"
    "pacebar pacebar pacebar";
}
/* When the Check Factor panel is on, insert a narrow column BETWEEN the
   sideLeft column and the hero AND a mirror column on the right so the
   hero stays visually centered. The mirror column is empty (reserved
   for a future Pace Target tile relocation) but reserves the same
   space so the hero doesn't drift right. */
.crewz-cockpit.landscape.cf-on .crewz-cockpit-hero-row {
  grid-template-columns:
    minmax(120px, 0.9fr)   /* sideL  */
    minmax(38px, 0.28fr)   /* cf     */
    minmax(0, 2.4fr)       /* hero   */
    minmax(38px, 0.28fr)   /* cf-mir */
    minmax(120px, 0.9fr);  /* sideR  */
  grid-template-areas:
    "sideL cf hero cfR sideR"
    "pacebar pacebar pacebar pacebar pacebar";
}
/* Session-Goal pie needs a wider slot than the narrow CF bar. Bump the
   left-tile column AND its right mirror so the hero stays centred. */
.crewz-cockpit.landscape.goal-on .crewz-cockpit-hero-row {
  grid-template-columns:
    minmax(120px, 0.9fr)   /* sideL  */
    minmax(90px, 0.75fr)   /* goal   */
    minmax(0, 2.2fr)       /* hero   */
    minmax(90px, 0.75fr)   /* goalR  */
    minmax(120px, 0.9fr);  /* sideR  */
}
.crewz-cockpit.portrait .crewz-cockpit-hero-row {
  grid-template-columns: 1fr;
  grid-template-rows: auto minmax(0, 1fr) auto auto;
  grid-template-areas:
    "sideL"
    "hero"
    "sideR"
    "pacebar";
}
/* In portrait the CF panel becomes a slim row between the sideL row and
   the hero, so it doesn't overwhelm the vertical budget. */
.crewz-cockpit.portrait.cf-on .crewz-cockpit-hero-row {
  grid-template-rows: auto auto minmax(0, 1fr) auto auto;
  grid-template-areas:
    "sideL"
    "cf"
    "hero"
    "sideR"
    "pacebar";
}
.crewz-cockpit-cf {
  grid-area: cf;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  /* Fully transparent — the panel exists to place its widget in the
     grid, not to draw its own chrome. Matches the see-through styling
     of the right flanking tiles. */
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 4px 2px;
}
.crewz-cockpit.dark .crewz-cockpit-cf {
  background: transparent;
  border: none;
}
.crewz-cockpit.portrait .crewz-cockpit-cf {
  padding: 4px 8px;
}
/* Landscape Session Goal: pie hugs the hero from the left and the pace
   thumb hugs it from the right in equal-width boxes → mirror symmetry. */
.crewz-cockpit.landscape.goal-on .crewz-cockpit-cf { justify-content: flex-end; }
.crewz-cockpit.landscape.goal-on .crewz-cockpit-cf > * { max-width: 96px; }
.crewz-cockpit-cfR {
  grid-area: cfR;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  min-height: 0;
  padding: 4px 2px;
}
.crewz-cockpit-cfR > .crewz-cockpit-hero-arrow.in-col {
  position: static;
  transform: none;
  width: 96px;
  max-width: 100%;
  display: flex;
  justify-content: center;
}
.crewz-cockpit-side {
  display: flex;
  gap: 8px;
  min-width: 0;
  min-height: 0;
}
.crewz-cockpit-side-left  { grid-area: sideL; }
.crewz-cockpit-side-right { grid-area: sideR; }
.crewz-cockpit.landscape .crewz-cockpit-side {
  flex-direction: column;
  justify-content: center;
}
.crewz-cockpit.portrait .crewz-cockpit-side {
  flex-direction: row;
  justify-content: space-around;
}
.crewz-cockpit-side-tile {
  flex: 1 1 auto;
  background: var(--c-bg);
  border: 1px solid var(--c-line);
  border-radius: 10px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}
.crewz-cockpit-hero {
  grid-area: hero;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
}
/* When a pace target is active, stack target · split · delta as a
   flex column so the target chip + delta pill don't overlap the huge
   split number. The metric itself sits in the middle and shrinks
   proportionally when there's less vertical budget. */
.crewz-cockpit-hero.with-pace {
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  padding: 4px 0;
}
.crewz-cockpit-hero-metric {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  width: 100%;
}
.crewz-cockpit-hero-dir {
  position: absolute;
  top: 50%;
  right: clamp(6px, 2vw, 22px);
  transform: translateY(-50%);
  font-size: clamp(36px, 6vw, 72px);
  color: var(--pace);
  text-shadow: 0 0 18px var(--pace), 0 0 6px var(--pace);
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
  user-select: none;
  z-index: 5;
}

/* ── Enhanced pace visualisation inside the hero ─────────────────────── */
/* Chevron arrows flanking the split number — one on each side. They
   share a shape but the colour + glyph are driven by pace state so
   they always agree with the ribbon below the hero. */
.crewz-cockpit-hero-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  /* Capped at ~40 px so the chevron matches the height of a
     sideRight metric tile (e.g. "CAL 10" reads ~34-40 px tall). */
  font-size: clamp(20px, 3vw, 40px);
  color: var(--pace);
  text-shadow:
    0 0 22px var(--pace),
    0 0 8px var(--pace);
  font-weight: 900;
  line-height: 1;
  pointer-events: none;
  user-select: none;
  z-index: 5;
  animation: crewz-pace-pulse 1.6s ease-in-out infinite;
}
.crewz-cockpit-pace-thumb {
  width: 1.15em;
  height: 1.15em;
  display: block;
  filter: drop-shadow(0 0 10px #21B86A) drop-shadow(0 0 4px #21B86A);
}
.crewz-cockpit-hero-arrow.left  { left:  clamp(6px, 2vw, 22px); }
.crewz-cockpit-hero-arrow.right { right: clamp(6px, 2vw, 22px); }

/* Small "TARGET · 1:55" chip pinned to the top of the hero. Reads as
   a mini header above the big split number. */
.crewz-cockpit-hero-target {
  position: relative;
  font-size: clamp(10px, 1.3vw, 13px);
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-dim);
  z-index: 5;
  white-space: nowrap;
  pointer-events: none;
  text-align: center;
}
.crewz-cockpit-hero-target strong {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: var(--pace);
  margin-left: 4px;
  letter-spacing: 0;
  font-weight: 800;
}

/* Big coloured "−16s AHEAD" delta pill under the split. This is the
   headline "how am I doing?" cue — carries most of the pace
   information at a glance. */
.crewz-cockpit-hero-delta {
  position: relative;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 14px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--pace) 18%, transparent);
  border: 1px solid color-mix(in srgb, var(--pace) 45%, transparent);
  color: var(--pace);
  z-index: 5;
  pointer-events: none;
  white-space: nowrap;
  text-shadow: 0 0 10px color-mix(in srgb, var(--pace) 60%, transparent);
  align-self: center;
}
.crewz-cockpit-hero-delta-val {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-weight: 900;
  font-size: clamp(18px, 2.6vw, 28px);
  line-height: 1;
  letter-spacing: -0.02em;
}
.crewz-cockpit-hero-delta-word {
  font-size: clamp(10px, 1.3vw, 13px);
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

@keyframes crewz-pace-pulse {
  0%, 100% { opacity: 0.85; transform: translateY(-50%) scale(1); }
  50%      { opacity: 1;    transform: translateY(-50%) scale(1.06); }
}
@media (prefers-reduced-motion: reduce) {
  .crewz-cockpit-hero-arrow { animation: none; }
}

.crewz-cockpit-zone-chip {
  grid-column: 1 / -1;
  justify-self: center;
  font-size: clamp(11px, 1.6vw, 14px);
  letter-spacing: .08em;
  color: var(--c, var(--c-dim));
  text-transform: uppercase;
  font-weight: 700;
  line-height: 1;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.crewz-cockpit-zone-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--c, var(--c-dim));
  box-shadow: 0 0 10px var(--c, transparent);
}

/* Hero text styling — adds the soft text-shadow that glows with the zone. */
.crewz-cockpit-hero .text-center,
.crewz-cockpit-hero [class*="text-8xl"],
.crewz-cockpit-hero [class*="text-9xl"],
.crewz-cockpit-hero [class*="text-7xl"],
.crewz-cockpit-hero [class*="text-6xl"] {
  text-shadow: 0 0 24px color-mix(in srgb, var(--glow, transparent) 28%, transparent);
}

/* Compact (active workout) — tighten paddings everywhere. */
.crewz-cockpit.cockpit-compact .crewz-cockpit-grid { gap: 6px; padding: 6px; }
.crewz-cockpit.cockpit-compact .crewz-cockpit-hero-row { gap: 8px; padding: 8px; }
.crewz-cockpit.cockpit-compact .crewz-cockpit-side { gap: 6px; }
.crewz-cockpit.cockpit-compact .crewz-cockpit-side-tile { padding: 6px; }

/* ── Pace-vs-target ribbon + controls ─────────────────────────────────────── */
.crewz-cockpit-pacebar {
  grid-area: pacebar;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding-top: 6px;
  border-top: 1px dashed var(--c-line);
  min-width: 0;
}
.crewz-cockpit-pace-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 540px;
}
.crewz-cockpit-pace-end {
  font-size: 10px;
  letter-spacing: .14em;
  color: var(--c-dim);
  opacity: .6;
  pointer-events: none;
  flex: none;
  text-transform: uppercase;
  font-weight: 600;
}
.crewz-cockpit-pace-track {
  position: relative;
  flex: 1 1 auto;
  height: 10px;
  border-radius: 6px;
  background: var(--c-panel);
  border: 1px solid var(--c-line);
  overflow: hidden;
}
.crewz-cockpit-pace-center {
  position: absolute;
  left: 50%;
  top: -2px;
  bottom: -2px;
  width: 2px;
  background: var(--c-dim);
  transform: translateX(-50%);
  opacity: .7;
  pointer-events: none;
}
.crewz-cockpit-pace-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  background: var(--pace);
  opacity: .45;
  transition: left .35s ease, width .35s ease;
}
.crewz-cockpit-pace-marker {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--pace);
  transform: translate(-50%, -50%);
  box-shadow: 0 0 12px var(--pace), 0 0 4px var(--pace);
  transition: left .35s ease;
}
.crewz-cockpit-pace-ctl {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  overflow: hidden;
  transition: opacity .35s ease, max-height .35s ease, transform .35s ease;
  max-height: 120px;
  opacity: 1;
  width: 100%;
}
.crewz-cockpit-pace-ctl.hidden {
  opacity: 0;
  max-height: 0;
  transform: translateY(6px);
  pointer-events: none;
  gap: 0;
}
.crewz-cockpit-pace-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--c-panel);
  border: 1px solid var(--c-line);
  border-radius: 999px;
  padding: 4px 12px 4px 5px;
  cursor: pointer;
  color: var(--c-dim);
  font: inherit;
  user-select: none;
}
.crewz-cockpit-pace-switch-knob {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--c-dim);
  transition: background .2s ease, box-shadow .2s ease;
}
.crewz-cockpit-pace-switch.on {
  border-color: #1D9E75;
  color: var(--c-text);
}
.crewz-cockpit-pace-switch.on .crewz-cockpit-pace-switch-knob {
  background: #3FD6A4;
  box-shadow: 0 0 10px #3FD6A4;
}
.crewz-cockpit-pace-switch-text {
  font-size: 11px;
  letter-spacing: .12em;
  font-weight: 700;
  text-transform: uppercase;
}
.crewz-cockpit-pace-target {
  display: flex;
  align-items: center;
  gap: 10px;
}
.crewz-cockpit-pace-step {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--c-line);
  background: var(--c-panel);
  color: var(--c-text);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}
.crewz-cockpit-pace-step:active { background: #1D9E75; color: #04140E; }
.crewz-cockpit-pace-target-val {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
}
.crewz-cockpit-pace-target-num {
  font-size: 26px;
  line-height: 1;
  font-weight: 700;
  color: var(--c-text);
  font-variant-numeric: tabular-nums;
}
.crewz-cockpit-pace-target-cap {
  font-size: 10px;
  letter-spacing: .08em;
  color: var(--pace);
  font-weight: 700;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
}
.crewz-cockpit-pace-hint {
  font-size: 12px;
  color: var(--c-dim);
}
.crewz-cockpit-pace-src {
  font-size: 10px;
  letter-spacing: .08em;
  color: var(--c-dim);
  text-transform: uppercase;
  opacity: .75;
  width: 100%;
  text-align: center;
  min-height: 1em;
}
.crewz-cockpit-pace-tap {
  font-size: 11px;
  letter-spacing: .14em;
  color: var(--c-dim);
  text-transform: uppercase;
  opacity: .55;
}
.crewz-cockpit-pace-ghost {
  background: transparent;
  border: 1px dashed var(--c-line);
  color: var(--c-dim);
  border-radius: 999px;
  padding: 4px 14px;
  font: inherit;
  font-size: 12px;
  letter-spacing: .1em;
  text-transform: uppercase;
  cursor: pointer;
}
.crewz-cockpit-pace-prog {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 12px;
  letter-spacing: .08em;
  color: var(--c-dim);
  text-transform: uppercase;
  font-weight: 700;
}
.crewz-cockpit-pace-prog strong {
  font-weight: 700;
  color: var(--c-text);
  font-size: 18px;
  font-variant-numeric: tabular-nums;
  letter-spacing: .02em;
}
.crewz-cockpit-pace-prog-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--pace);
  box-shadow: 0 0 10px var(--pace);
}
.crewz-cockpit-pace-prog-delta {
  font-variant-numeric: tabular-nums;
  color: var(--pace);
}
@media (prefers-reduced-motion: reduce) {
  .crewz-cockpit-pace-fill,
  .crewz-cockpit-pace-marker,
  .crewz-cockpit-pace-ctl { transition: none; }
}

/* Swipe-down-to-exit hint — only rendered in cockpit-fullscreen mode.
   Absolutely positioned so this decorative hint stays OUT of the grid row
   flow. (When it was a grid child it consumed a row and pushed the three
   top tiles down into the greedy 1fr row, leaving a large vacant gap under
   the top section in fullscreen portrait — 2026-06 bug report.) */
.crewz-cockpit-swipe-hint {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 38px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity .35s ease;
  pointer-events: none;
}
.crewz-cockpit-swipe-hint.visible { opacity: 0.85; }
.crewz-cockpit-swipe-handle {
  width: 38px;
  height: 4px;
  border-radius: 999px;
  background: var(--c-dim);
  opacity: .55;
}
.crewz-cockpit-swipe-label {
  font-size: 10px;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: var(--c-dim);
  font-weight: 600;
}
@media (prefers-reduced-motion: reduce) {
  .crewz-cockpit-swipe-hint { transition: none; }
}
`;
